import React, { useEffect, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/config/firebase';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';

interface WizardStep {
    type: 'QUESTION' | 'INTRODUCTION' | 'CONCLUSION';
    content?: string;
    groupTitle?: string;
    behaviorTitle?: string;
    indicatorId: string;
    indicatorDescription?: string;
    question?: string;
    scale?: any;
}

export const ExternalEvaluationPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [evaluationData, setEvaluationData] = useState<any>(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState<Record<string, number>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const fetchEvaluation = async () => {
            if (!token) return;
            try {
                const functions = getFunctions(app, 'us-central1');
                const getExternalEvaluation = httpsCallable(functions, 'getExternalEvaluation');
                const result = await getExternalEvaluation({ token });
                setEvaluationData(result.data);
            } catch (err: any) {
                setError(err.message || 'Erro ao carregar a avaliação.');
            } finally {
                setLoading(false);
            }
        };
        fetchEvaluation();
    }, [token]);

    const steps = useMemo(() => {
        if (!evaluationData) return [];
        const { campaign, evaluateeName } = evaluationData;
        const flattened: WizardStep[] = [];

        // 1. Introduction
        const introText = campaign.introductionLeader;
        if (introText) {
            flattened.push({
                type: 'INTRODUCTION',
                content: introText,
                indicatorId: 'intro'
            });
        }

        // 2. Questions (Sorted behaviors)
        const sortedGroups = [...(campaign.criteriaSnapshot || [])].sort((a: any, b: any) => {
            const getOrder = (type?: string) => {
                if (type === 'PROLOGUE') return 0;
                if (type === 'EPILOGUE') return 2;
                return 1;
            };
            return getOrder(a.type) - getOrder(b.type);
        });

        sortedGroups.forEach((group: any) => {
            group.behaviors.forEach((behavior: any) => {
                behavior.indicators.forEach((indicator: any) => {
                    if (!indicator.isActive) return;

                    let scale = (campaign.scalesSnapshot || []).find((s: any) => s.id === indicator.scaleId);
                    if (!scale && campaign.scalesSnapshot?.length > 0) {
                        scale = campaign.scalesSnapshot[0];
                    }

                    flattened.push({
                        type: 'QUESTION',
                        groupTitle: group.description,
                        behaviorTitle: behavior.description,
                        indicatorId: indicator.id,
                        indicatorDescription: indicator.description,
                        // External client acts as a 'LEADER' conceptually
                        question: indicator.leaderEvaluationQuestion || indicator.description,
                        scale: scale
                    });
                });
            });
        });

        // 3. Conclusion
        const conclusionText = campaign.conclusionLeader;
        if (conclusionText) {
            flattened.push({
                type: 'CONCLUSION',
                content: conclusionText,
                indicatorId: 'conclusion'
            });
        }

        return flattened;
    }, [evaluationData]);

    const handleAnswer = (value: number) => {
        const step = steps[currentStep];
        setAnswers(prev => ({ ...prev, [step.indicatorId]: value }));

        // Auto-advance
        if (currentStep < steps.length - 1) {
            setTimeout(() => {
                setCurrentStep(prev => prev + 1);
            }, 300);
        }
    };

    const handleSave = async () => {
        if (!token) return;
        setIsSaving(true);
        console.log("Submitting external evaluation for token:", token);
        console.log("Answers:", answers);
        try {
            const functions = getFunctions(app, 'us-central1');
            const submitExternalEvaluation = httpsCallable(functions, 'submitExternalEvaluation');
            const result = await submitExternalEvaluation({ token, answers });
            console.log("Submission successful:", result);
            setSubmitted(true);
        } catch (err: any) {
            console.error("Submission failed:", err);
            setError(err.message || 'Erro ao enviar a avaliação.');
        } finally {
            setIsSaving(false);
        }
    };

    // Scroll Management
    useEffect(() => {
        const element = document.getElementById('wizard-content-stage');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [currentStep]);


    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (error && !submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-red-50 p-6 rounded-lg max-w-md w-full border border-red-100 shadow-sm">
                    <h2 className="text-red-800 text-lg font-bold mb-2">Acesso Negado</h2>
                    <p className="text-red-600">{error}</p>
                </div>
            </div>
        );
    }

    if (submitted) {
        const conclusionStep = steps.find(s => s.type === 'CONCLUSION');
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircleIcon className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Avaliação Concluída!</h2>
                    <div className="prose prose-indigo mx-auto">
                        <ReactMarkdown>{conclusionStep?.content || 'Obrigado por participar!'}</ReactMarkdown>
                    </div>
                </div>
            </div>
        );
    }

    const currentStepData = steps[currentStep];
    if (!currentStepData) return null; // Guard against empty steps while loading
    const isLastStep = currentStep === steps.length - 1;
    const progress = steps.length > 0 ? ((currentStep + 1) / steps.length) * 100 : 0;

    const variants = {
        enter: { x: 50, opacity: 0, scale: 0.95 },
        center: { x: 0, opacity: 1, scale: 1, zIndex: 1, transition: { duration: 0.4, ease: "easeInOut" } },
        exit: (direction: number) => ({
            x: direction < 0 ? 50 : -50,
            opacity: 0,
            scale: 0.95,
            filter: 'blur(10px)',
            zIndex: 0,
            transition: { duration: 0.3, ease: "easeIn" }
        })
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pb-20" id="wizard-container">
            <div className="bg-indigo-900 text-white p-6 shadow-md text-center">
                <h1 className="text-2xl font-bold">Avaliação de Desempenho</h1>
                <p className="opacity-80">Referente a {evaluationData?.evaluateeName}</p>
            </div>

            <div id="wizard-content-stage" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
                {/* Progress Bar */}
                <div className="mb-8 px-2">
                    <div className="flex justify-between items-end mb-2">
                        <span className="text-sm font-semibold text-indigo-900">
                            Etapa {currentStep + 1} de {steps.length - 1}
                        </span>
                    </div>
                    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-indigo-600 transition-all duration-500 ease-out"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                <motion.div layout className="relative overflow-visible" transition={{ duration: 0.3 }}>
                    <AnimatePresence initial={false} mode="wait" custom={1}>
                        <motion.div
                            key={currentStep}
                            custom={1}
                            variants={variants as any}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            className="w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                            layout
                        >
                            <div className="p-8 md:p-10">
                                {currentStepData.type === 'INTRODUCTION' ? (
                                    <div className="prose prose-indigo max-w-none">
                                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Bem-vindo à avaliação</h2>
                                        <ReactMarkdown>{currentStepData.content || ''}</ReactMarkdown>
                                    </div>
                                ) : currentStepData.type === 'QUESTION' ? (
                                    <>
                                        {/* Question Metadata */}
                                        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                                {currentStepData.groupTitle}
                                            </span>
                                            <span className="hidden md:inline text-gray-300">|</span>
                                            <span className="text-sm font-medium text-gray-500">
                                                {currentStepData.behaviorTitle}
                                            </span>
                                        </div>

                                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight mb-8">
                                            {currentStepData.question}
                                        </h2>

                                        {/* Options */}
                                        <div className="grid grid-cols-1 gap-3">
                                            {currentStepData.scale?.options.sort((a: any, b: any) => a.value - b.value).map((option: any) => {
                                                const isSelected = answers[currentStepData.indicatorId] === option.value;
                                                const label = option.labelLeader || option.label;
                                                const description = option.descriptionLeader || option.description;

                                                return (
                                                    <button
                                                        key={option.value}
                                                        onClick={() => handleAnswer(option.value)}
                                                        className={`relative w-full text-left p-4 md:p-5 rounded-xl border-2 transition-all duration-200 group
                                                            ${isSelected
                                                                ? 'border-indigo-600 bg-indigo-50/50 shadow-sm ring-1 ring-indigo-600 z-10'
                                                                : 'border-gray-100 hover:border-indigo-200 hover:bg-gray-50'
                                                            }
                                                        `}
                                                    >
                                                        <div className="flex items-start gap-4">
                                                            <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors mt-0.5
                                                                ${isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300 group-hover:border-indigo-400'}
                                                            `}>
                                                                {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                                                            </div>
                                                            <div className="flex-1">
                                                                <span className={`font-bold ${isSelected ? 'text-indigo-900' : 'text-gray-700'}`}>{label}</span>
                                                                {description && (
                                                                    <p className={`text-sm mt-1 leading-relaxed ${isSelected ? 'text-indigo-800/80' : 'text-gray-500'}`}>
                                                                        {description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </motion.div>

                {/* Navigation Actions */}
                <div className="mt-8 flex justify-between items-center px-2">
                    <button
                        onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                        disabled={currentStep === 0}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors
                            ${currentStep === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-indigo-600 hover:bg-white'}
                        `}
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Anterior
                    </button>

                    <button
                        onClick={() => {
                            if (!isLastStep) {
                                setCurrentStep(currentStep + 1);
                            } else {
                                handleSave();
                            }
                        }}
                        disabled={
                            (currentStepData.type === 'QUESTION' && (!answers[currentStepData.indicatorId] || isSaving)) ||
                            isSaving
                        }
                        className={`flex items-center gap-2 px-8 py-3 rounded-full font-bold shadow-lg shadow-indigo-200 transition-all 
                             ${(currentStepData.type === 'QUESTION' && !answers[currentStepData.indicatorId]) || isSaving
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-105 active:scale-95'
                            }
                         `}
                    >
                        {isSaving ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Enviando...
                            </>
                        ) : (
                            currentStepData.type === 'INTRODUCTION' ? 'Começar' :
                                isLastStep ? 'Finalizar' : 'Próximo'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
